import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  Check,
  ChevronDown,
  Download,
  FileText,
  Loader2,
  Paperclip,
  Plus,
  Tag as TagIcon,
  Trash2,
  X,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FolderUploadButton } from '@/components/ui/FolderUploadButton';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { AssigneesPicker } from '@/components/projects/AssigneesPicker';
import { useAuth } from '@/app/AuthProvider';
import { useProfiles } from '@/features/users/hooks';
import { cn } from '@/lib/utils';
import type { CalendarColor } from '@/types/database';
import {
  CALENDAR_COLORS,
  CALENDAR_COLOR_LABELS,
  CALENDAR_DOT_CLASS,
} from '@/features/calendar/colors';
import {
  getCalendarAttachmentSignedUrl,
  useCalendarEventAttachments,
  useCalendarTags,
  useCreateCalendarEvent,
  useCreateCalendarTag,
  useDeleteCalendarAttachment,
  useDeleteCalendarEvent,
  useDeleteCalendarTag,
  useUpdateCalendarEvent,
  useUploadCalendarAttachment,
  type CalendarEventWithDetails,
} from '@/features/calendar/hooks';

const MAX_ATTACHMENT_BYTES = 100 * 1024 * 1024; // 100 MB, same as tasks

interface EventDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Existing event to edit, or null to create a new one. */
  event: CalendarEventWithDetails | null;
  /** Pre-filled date (`YYYY-MM-DD`) when creating from a day cell. */
  defaultDate?: string;
}

/**
 * Create / edit pop-up for a calendar event. All fields from the spec:
 * title, date, optional start/end time, attendees ("who"), location,
 * tag (with inline create + delete), colour, notes, and — once the
 * event exists — file attachments.
 */
export function EventDialog({
  open,
  onOpenChange,
  event,
  defaultDate,
}: EventDialogProps) {
  const isEdit = !!event;

  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [location, setLocation] = useState('');
  const [notes, setNotes] = useState('');
  const [tagId, setTagId] = useState<string | null>(null);
  const [color, setColor] = useState<CalendarColor>('blue');
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);

  // Re-seed the form every time the dialog opens (or switches target).
  useEffect(() => {
    if (!open) return;
    setTitle(event?.title ?? '');
    setDate(event?.event_date ?? defaultDate ?? todayKey());
    setStartTime(event?.start_time?.slice(0, 5) ?? '');
    setEndTime(event?.end_time?.slice(0, 5) ?? '');
    setLocation(event?.location ?? '');
    setNotes(event?.notes ?? '');
    setTagId(event?.tag_id ?? null);
    setColor(event?.color ?? 'blue');
    setAttendeeIds(event?.attendees.map((a) => a.id) ?? []);
  }, [open, event, defaultDate]);

  const { data: profiles } = useProfiles();
  const members = useMemo(
    () =>
      (profiles ?? [])
        .filter((p) => p.is_active && p.role !== 'external_lender')
        .map((p) => ({
          id: p.id,
          full_name: p.full_name,
          email: p.email,
          avatar_url: p.avatar_url,
        })),
    [profiles],
  );
  const attendees = useMemo(
    () => members.filter((m) => attendeeIds.includes(m.id)),
    [members, attendeeIds],
  );

  const createEvent = useCreateCalendarEvent();
  const updateEvent = useUpdateCalendarEvent();
  const deleteEvent = useDeleteCalendarEvent();
  const saving = createEvent.isPending || updateEvent.isPending;

  const handleSave = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('Give the event a title.');
      return;
    }
    if (!date) {
      toast.error('Pick a date.');
      return;
    }
    if (endTime && !startTime) {
      toast.error('Set a start time before an end time.');
      return;
    }
    const input = {
      title: trimmed,
      event_date: date,
      start_time: startTime || null,
      end_time: endTime || null,
      location: location.trim() || null,
      notes: notes.trim() || null,
      tag_id: tagId,
      color,
      attendeeIds,
    };
    try {
      if (event) {
        await updateEvent.mutateAsync({ id: event.id, ...input });
        toast.success('Event updated.');
      } else {
        await createEvent.mutateAsync(input);
        toast.success('Event created.');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save the event.');
    }
  };

  const handleDelete = async () => {
    if (!event) return;
    if (!window.confirm(`Delete “${event.title}”? This also removes its attachments.`)) {
      return;
    }
    try {
      await deleteEvent.mutateAsync(event.id);
      toast.success('Event deleted.');
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the event.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit event' : 'New event'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="event-title">Title</Label>
            <Input
              id="event-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Supplier call"
              autoFocus
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="event-date">Date</Label>
              <Input
                id="event-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="event-start">Start time</Label>
              <Input
                id="event-start"
                type="time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="event-end">End time</Label>
              <Input
                id="event-end"
                type="time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                disabled={!startTime}
              />
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label>Who</Label>
            <AssigneesPicker
              assignees={attendees}
              members={members}
              onChange={setAttendeeIds}
            />
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="event-location">Location</Label>
            <Input
              id="event-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Main office, Zoom…"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Tag</Label>
              <TagPicker value={tagId} onChange={setTagId} />
            </div>
            <div className="grid gap-1.5">
              <Label>Color</Label>
              <div className="flex h-10 items-center gap-1.5">
                {CALENDAR_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={CALENDAR_COLOR_LABELS[c]}
                    aria-label={`Color ${CALENDAR_COLOR_LABELS[c]}`}
                    onClick={() => setColor(c)}
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full transition-transform hover:scale-110',
                      CALENDAR_DOT_CLASS[c],
                      color === c && 'ring-2 ring-ring ring-offset-2 ring-offset-background',
                    )}
                  >
                    {color === c && <Check className="h-3.5 w-3.5 text-white" />}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid gap-1.5">
            <Label htmlFor="event-notes">Notes</Label>
            <Textarea
              id="event-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Agenda, links, anything the team should know…"
            />
          </div>

          {isEdit && event ? (
            <AttachmentsSection eventId={event.id} />
          ) : null}
        </div>

        <DialogFooter className="sm:justify-between">
          {isEdit ? (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteEvent.isPending || saving}
            >
              {deleteEvent.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              {isEdit ? 'Save changes' : 'Create event'}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function todayKey(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

// ---------------------------------------------------------------------------
// Tag picker — select / clear / create / delete tags inline.
// ---------------------------------------------------------------------------

function TagPicker({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (tagId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<CalendarColor>('blue');

  const { data: tags } = useCalendarTags();
  const createTag = useCreateCalendarTag();
  const deleteTag = useDeleteCalendarTag();

  const selected = (tags ?? []).find((t) => t.id === value) ?? null;

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const tag = await createTag.mutateAsync({ name, color: newColor });
      onChange(tag.id);
      setNewName('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not create the tag.');
    }
  };

  const handleDelete = async (tagIdToDelete: string, name: string) => {
    if (!window.confirm(`Delete the tag “${name}”? Events keep their data but lose this tag.`)) {
      return;
    }
    try {
      await deleteTag.mutateAsync(tagIdToDelete);
      if (value === tagIdToDelete) onChange(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not delete the tag.');
    }
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-input bg-card px-3 text-sm transition-colors hover:bg-secondary/40 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
        >
          {selected ? (
            <span className="flex min-w-0 items-center gap-2">
              <span
                className={cn(
                  'h-2.5 w-2.5 shrink-0 rounded-full',
                  CALENDAR_DOT_CLASS[selected.color],
                )}
              />
              <span className="truncate">{selected.name}</span>
            </span>
          ) : (
            <span className="flex items-center gap-2 text-muted-foreground">
              <TagIcon className="h-3.5 w-3.5" />
              No tag
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72 p-0">
        <div className="max-h-56 overflow-y-auto p-1">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-secondary/60"
          >
            <X className="h-3.5 w-3.5" />
            No tag
            {value === null && <Check className="ml-auto h-4 w-4" />}
          </button>
          {(tags ?? []).map((tag) => (
            <div
              key={tag.id}
              className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-secondary/60"
            >
              <button
                type="button"
                onClick={() => {
                  onChange(tag.id);
                  setOpen(false);
                }}
                className="flex min-w-0 flex-1 items-center gap-2 text-left"
              >
                <span
                  className={cn(
                    'h-2.5 w-2.5 shrink-0 rounded-full',
                    CALENDAR_DOT_CLASS[tag.color],
                  )}
                />
                <span className="truncate">{tag.name}</span>
                {value === tag.id && <Check className="ml-auto h-4 w-4 shrink-0" />}
              </button>
              <button
                type="button"
                aria-label={`Delete tag ${tag.name}`}
                onClick={() => void handleDelete(tag.id, tag.name)}
                className="shrink-0 rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <div className="border-t border-border p-2">
          <div className="flex items-center gap-1.5">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleCreate();
                }
              }}
              placeholder="New tag…"
              className="h-8 w-full min-w-0 flex-1 rounded-md border border-input bg-card px-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void handleCreate()}
              disabled={!newName.trim() || createTag.isPending}
            >
              {createTag.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Add
            </Button>
          </div>
          <div className="mt-2 flex items-center gap-1">
            {CALENDAR_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`Tag color ${CALENDAR_COLOR_LABELS[c]}`}
                onClick={() => setNewColor(c)}
                className={cn(
                  'h-4 w-4 rounded-full transition-transform hover:scale-110',
                  CALENDAR_DOT_CLASS[c],
                  newColor === c && 'ring-2 ring-ring ring-offset-1 ring-offset-background',
                )}
              />
            ))}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------------------
// Attachments — list, upload, download, delete. Mirrors the task
// drawer's attachments section.
// ---------------------------------------------------------------------------

function AttachmentsSection({ eventId }: { eventId: string }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { profile } = useAuth();
  const { data: attachments, isLoading } = useCalendarEventAttachments(eventId);
  const upload = useUploadCalendarAttachment();
  const remove = useDeleteCalendarAttachment();

  const handleFiles = async (files: FileList | File[] | null) => {
    if (!files || files.length === 0) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`“${file.name}” is larger than 100 MB.`);
        continue;
      }
      try {
        await upload.mutateAsync({ eventId, file });
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : `Could not upload “${file.name}”.`,
        );
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownload = async (path: string) => {
    try {
      const url = await getCalendarAttachmentSignedUrl(path);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not open the file.');
    }
  };

  return (
    <div className="grid gap-1.5">
      <div className="flex items-center justify-between">
        <Label>Attachments</Label>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={upload.isPending}
          >
            {upload.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Paperclip className="h-3.5 w-3.5" />
            )}
            Attach files
          </Button>
          <FolderUploadButton
            size="sm"
            disabled={upload.isPending}
            onFiles={(files) => void handleFiles(files)}
          />
        </div>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>
      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading attachments…</p>
      ) : (attachments ?? []).length === 0 ? (
        <p className="text-xs text-muted-foreground">No files attached yet.</p>
      ) : (
        <ul className="grid gap-1">
          {(attachments ?? []).map((a) => (
            <li
              key={a.id}
              className="flex items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 text-sm"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate" title={a.file_name}>
                {a.file_name}
              </span>
              {a.size_bytes != null && (
                <span className="shrink-0 text-xs text-muted-foreground">
                  {formatBytes(a.size_bytes)}
                </span>
              )}
              <button
                type="button"
                aria-label={`Download ${a.file_name}`}
                onClick={() => void handleDownload(a.storage_path)}
                className="shrink-0 rounded p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              {(a.uploaded_by === profile?.id || profile?.role === 'super_admin') && (
                <button
                  type="button"
                  aria-label={`Delete ${a.file_name}`}
                  onClick={() =>
                    void remove
                      .mutateAsync({
                        id: a.id,
                        eventId,
                        storagePath: a.storage_path,
                      })
                      .catch((err: unknown) =>
                        toast.error(
                          err instanceof Error ? err.message : 'Could not delete the file.',
                        ),
                      )
                  }
                  className="shrink-0 rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
